import React from 'react';
import { useTranslation } from 'react-i18next';
import { Container, Typography, Grid, Card, CardContent, Button, Box, Stack, Chip, Divider, CircularProgress } from '@mui/material';
import LaunchIcon from '@mui/icons-material/Launch';
import { motion } from 'framer-motion';
import { useCases } from '../../hooks/useCases';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import { Link } from 'react-router-dom';
import EditableImage from '../../components/ui/EditableImage';

const CasesPage = () => {
  const { t, i18n } = useTranslation();
  const { data: cases, isLoading: loadingCases } = useCases({ isPublished: true, language: i18n.language });

  return (
    <Box component="main" sx={{ bgcolor: '#0B0F14', minHeight: '100vh', pb: 20 }}>
      {/* Header Section */}
      <Box sx={{ py: 15, bgcolor: '#111827', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
        <Container maxWidth="md">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Typography 
              variant="overline" 
              align="center"
              sx={{ color: '#D4AF37', fontWeight: 700, letterSpacing: 3, display: 'block', mb: 2 }}
            >
              {t('navBarMenu.expertise.cases').toUpperCase()}
            </Typography>
            <Typography 
              variant="h1" 
              align="center"
              sx={{ color: 'white', mb: 3, fontFamily: 'Philosopher', fontSize: { xs: '2.5rem', md: '3.5rem' } }}
            >
              {t("casesPage.header.titlePrefix")} <span style={{ color: '#0FA3A3' }}>{t("casesPage.header.titleHighlight")}</span>
            </Typography>
            <Typography 
              variant="h6" 
              align="center"
              sx={{ color: '#9CA3AF', fontWeight: 300, lineHeight: 1.6, maxWidth: '700px', mx: 'auto' }}
            >
              {t("casesPage.header.subtitle")}
            </Typography>
          </motion.div>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ mt: 8 }}>
        <Grid container spacing={6}>
          {loadingCases ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', py: 10 }}>
              <CircularProgress sx={{ color: '#0FA3A3' }} />
            </Box>
          ) : cases?.map((study, idx) => (
            <Grid item xs={12} md={6} key={study.id}>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: idx * 0.1 }}
              >
                <Card sx={{ 
                  bgcolor: '#111827', 
                  borderRadius: '28px', 
                  overflow: 'hidden', 
                  border: '1px solid rgba(255,255,255,0.05)',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  '&:hover': {
                    borderColor: '#D4AF37',
                    transform: 'translateY(-10px)',
                    boxShadow: '0 20px 40px rgba(0,0,0,0.4)'
                  }
                }}>
                  <Box sx={{ height: '240px', overflow: 'hidden', position: 'relative' }}>
                    <EditableImage 
                      src={study.cover_image_url || "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&q=80"}
                      alt={study.title}
                      tableName="project_cases"
                      recordId={study.id}
                      imageField="cover_image_url"
                      sx={{ height: '100%', width: '100%' }}
                      imgSx={{ 
                        transition: 'transform 0.6s ease',
                        filter: 'brightness(0.7)'
                      }}
                      initialObjectFit={study.object_fit || 'cover'}
                      initialObjectPosition={study.object_position || '50% 50%'}
                    />
                    <Box sx={{ 
                      position: 'absolute', 
                      top: 20, 
                      left: 20, 
                      bgcolor: 'rgba(15, 163, 163, 0.9)', 
                      color: 'white', 
                      px: 2, 
                      py: 0.5, 
                      borderRadius: '8px',
                      fontWeight: 700,
                      fontSize: '0.75rem',
                      backdropFilter: 'blur(4px)',
                      zIndex: 1
                    }}>
                      {study.case_label}
                    </Box>
                  </Box>
                  <CardContent sx={{ p: 5, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="h4" component="div" sx={{ color: 'white', fontWeight: 800, mb: 2, fontFamily: 'Philosopher' }}>
                      <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>
                        {study.title}
                      </ReactMarkdown>
                    </Typography>
                    <Typography variant="body1" component="div" sx={{ color: '#9CA3AF', mb: 3, lineHeight: 1.7, flexGrow: 1 }}>
                      <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>
                        {study.excerpt}
                      </ReactMarkdown>
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 4 }}>
                      {study.technologies?.map(tech => (
                        <Chip 
                          key={tech.technology?.id} 
                          label={tech.technology?.name} 
                          size="small" 
                          sx={{ bgcolor: 'rgba(255,255,255,0.05)', color: '#D4AF37', border: '1px solid rgba(212, 175, 55, 0.2)' }} 
                        />
                      ))}
                    </Box>
                    <Divider sx={{ borderColor: 'rgba(255,255,255,0.05)', mb: 3 }} />
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Box>
                        <Typography variant="caption" sx={{ color: '#9CA3AF', display: 'block', textTransform: 'uppercase', letterSpacing: 1 }}>
                          {t("casesPage.card.resultLabel")}
                        </Typography>
                        <Typography variant="h6" component="div" sx={{ color: '#0FA3A3', fontWeight: 800 }}>
                          <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>
                            {study.result}
                          </ReactMarkdown>
                        </Typography>
                      </Box>
                      <Button 
                        endIcon={<LaunchIcon />}
                        component={Link}
                        to={`/services/${study.slug}`}
                        sx={{ color: '#D4AF37', fontWeight: 700, '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' } }}
                      >
                        {t("casesPage.card.viewCase")}
                      </Button>
                    </Stack>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>

        {/* Call to Action Section */}
        <Box sx={{ 
          mt: 15,
          p: { xs: 6, md: 10 }, 
          background: 'linear-gradient(135deg, #1F2937 0%, #111827 100%)', 
          borderRadius: '40px', 
          textAlign: 'center', 
          border: '1px solid rgba(255,255,255,0.05)',
          boxShadow: '0 30px 60px rgba(0,0,0,0.5)',
          position: 'relative',
          overflow: 'hidden'
        }}>
          {/* Decorative element */}
          <Box sx={{ 
            position: 'absolute', 
            top: -100, 
            right: -100, 
            width: '300px', 
            height: '300px', 
            borderRadius: '50%', 
            background: 'radial-gradient(circle, rgba(15, 163, 163, 0.1) 0%, transparent 70%)',
            zIndex: 0
          }} />

          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Typography variant="h2" sx={{ color: 'white', mb: 3, fontFamily: 'Philosopher', fontWeight: 700, fontSize: { xs: '2rem', md: '3rem' } }}>
              {t("casesPage.cta.titlePrefix")}{" "}
              <span style={{ color: '#0FA3A3' }}>{t("casesPage.cta.titleHighlight1")}</span>{" "}
              {t("casesPage.cta.titleMiddle")}{" "}
              <span style={{ color: '#D4AF37' }}>{t("casesPage.cta.titleHighlight2")}</span>
            </Typography>
            <Typography variant="h6" sx={{ color: '#9CA3AF', mb: 6, maxWidth: '800px', mx: 'auto', fontWeight: 300, lineHeight: 1.6 }}>
              {t("casesPage.cta.subtitle")}
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} justifyContent="center">
              <Button 
                variant="contained" 
                size="large"
                href="https://calendly.com/sanoualexandreism/30min"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ 
                  bgcolor: '#D4AF37', 
                  color: '#0B0F14', 
                  px: 6, 
                  py: 2.5, 
                  borderRadius: '16px', 
                  fontWeight: 800,
                  fontSize: '1.1rem',
                  textTransform: 'none',
                  boxShadow: '0 10px 20px rgba(212, 175, 55, 0.2)',
                  '&:hover': { 
                    bgcolor: '#B8962E',
                    transform: 'translateY(-3px)',
                    boxShadow: '0 15px 30px rgba(212, 175, 55, 0.3)'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                {t("casesPage.cta.primaryButton")}
              </Button>
              <Button 
                variant="outlined" 
                size="large"
                component={Link}
                to="/contact"
                sx={{ 
                  borderColor: 'rgba(255,255,255,0.1)', 
                  color: 'white', 
                  px: 6, 
                  py: 2.5, 
                  borderRadius: '16px',
                  fontWeight: 800,
                  fontSize: '1.1rem',
                  textTransform: 'none',
                  backdropFilter: 'blur(10px)',
                  '&:hover': { 
                    borderColor: 'white', 
                    bgcolor: 'rgba(255,255,255,0.05)', 
                    transform: 'translateY(-3px)' 
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                {t("casesPage.cta.secondaryButton")}
              </Button>
            </Stack>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default CasesPage;
